import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { bankingAPI, accountsAPI } from '../../services/api';
import { useCompany } from '../common/useCompany';
import PrintButton from '../../utils/print';
import PrintLetterhead from '../common/PrintLetterhead';
import toast from 'react-hot-toast';
import '../../styles/data-form.css';
import { fmtMoney } from '../../utils/currency';

const fmt = fmtMoney;
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d + 'T00:00:00');
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

/** Convert a number to words for the check line */
function numberToWords(n) {
  if (n === 0) return 'Zero';
  const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine','Ten',
    'Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
  const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
  const big = ['','Thousand','Million','Billion'];
  const intPart = Math.floor(n);
  const decPart = Math.round((n - intPart) * 100);
  let words = '';
  if (intPart === 0) { words = 'Zero'; }
  else {
    const str = String(intPart);
    const groups = [];
    for (let i = str.length; i > 0; i -= 3) groups.unshift(str.slice(Math.max(0, i - 3), i));
    groups.forEach((g, idx) => {
      const num = parseInt(g, 10);
      if (num === 0) return;
      const h = Math.floor(num / 100);
      const t = num % 100;
      let part = '';
      if (h > 0) part += ones[h] + ' Hundred';
      if (t > 0) { if (part) part += ' '; part += t < 20 ? ones[t] : tens[Math.floor(t / 10)] + (t % 10 ? '-' + ones[t % 10] : ''); }
      if (part) words += (words ? ' ' : '') + part + ' ' + big[groups.length - 1 - idx];
    });
  }
  if (decPart > 0) {
    const d1 = Math.floor(decPart / 10);
    const d2 = decPart % 10;
    words += ' and ' + (d1 > 0 ? tens[d1] + (d2 ? '-' + ones[d2] : '') : ones[d2]) + ' Cents';
  } else {
    words += ' and No Cents';
  }
  return words;
}

export default function WriteCheck() {
  const navigate = useNavigate();
  const company = useCompany();
  const checkRef = useRef(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    bank_account_id: '',
    check_number: '',
    check_date: new Date().toISOString().split('T')[0],
    payee: '',
    payee_type: 'Other',
    amount: '',
    expense_account_id: '',
    memo: '',
  });

  useEffect(() => {
    bankingAPI.accounts().then(setBankAccounts).catch(() => {});
    accountsAPI.list({ type: 'Expense' }).then(setGlAccounts).catch(() => {});
  }, []);

  const u = (f) => (e) => setForm({ ...form, [f]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await bankingAPI.createCheck({
        ...form,
        amount: parseFloat(form.amount) || 0,
      });
      toast.success('Check created successfully');
      navigate('/banking/transactions');
    } catch (err) {
      toast.error(err.message);
    }
    setLoading(false);
  };

  const amount = parseFloat(form.amount) || 0;
  const words = amount > 0 ? numberToWords(amount) : '';

  return (
    <div>
      <div className="toolbar">
        <h2>Write Check</h2>
        <div className="toolbar-right">
          <PrintButton targetRef={checkRef} title={`Check #${form.check_number || 'New'}`} />
          <button className="btn btn-outline" onClick={() => navigate('/banking')}>Cancel</button>
          <button className="btn btn-primary" form="check-form" disabled={loading}>
            {loading ? 'Saving...' : 'Write Check'}
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 20 }}>
        {/* Check Form */}
        <div className="df-editor" style={{ flex: 1 }}>
          <div className="df-section-header"><span>Check Details</span></div>
          <form id="check-form" onSubmit={handleSubmit}>
            <div className="df-body">
              <div className="df-grid cols-2">
                <div className="df-field">
                  <label>Bank Account <span className="req">*</span></label>
                  <select required value={form.bank_account_id} onChange={u('bank_account_id')}>
                    <option value="">Select bank account...</option>
                    {bankAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.bank_name || a.account_name} ({fmt(a.bank_balance)})</option>
                    ))}
                  </select>
                </div>
                <div className="df-field">
                  <label>Check Number <span className="req">*</span></label>
                  <input required value={form.check_number} onChange={u('check_number')} placeholder="e.g. 1001" />
                </div>
                <div className="df-field">
                  <label>Check Date <span className="req">*</span></label>
                  <input type="date" required value={form.check_date} onChange={u('check_date')} />
                </div>
                <div className="df-field">
                  <label>Payee <span className="req">*</span></label>
                  <input required value={form.payee} onChange={u('payee')} placeholder="Who is this check for?" />
                </div>
                <div className="df-field">
                  <label>Payee Type</label>
                  <select value={form.payee_type} onChange={u('payee_type')}>
                    <option value="Vendor">Vendor</option>
                    <option value="Employee">Employee</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div className="df-field">
                  <label>Amount <span className="req">*</span></label>
                  <input type="number" step="0.01" min="0.01" required value={form.amount} onChange={u('amount')} placeholder="0.00" />
                </div>
                <div className="df-field">
                  <label>Expense Account <span className="req">*</span></label>
                  <select required value={form.expense_account_id} onChange={u('expense_account_id')}>
                    <option value="">Select account...</option>
                    {glAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
                    ))}
                  </select>
                </div>
                <div className="df-field">
                  <label>Memo</label>
                  <input value={form.memo} onChange={u('memo')} placeholder="Optional memo" />
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Check Preview */}
        <div style={{ flex: 1 }} ref={checkRef}>
          <div className="card print-preview" style={{ padding: 0, overflow: 'hidden' }}>
            <PrintLetterhead />
            <div style={{ padding: '20px 24px' }}>
              {/* Check number & date */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Check No.</div>
                  <div style={{ fontSize: 14, fontWeight: 600, fontFamily: 'monospace' }}>{form.check_number || '—'}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Date</div>
                  <div style={{ fontSize: 13 }}>{fmtDate(form.check_date)}</div>
                </div>
              </div>

              {/* Pay to */}
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Pay to the Order of</div>
                <div style={{ fontSize: 18, fontWeight: 600, borderBottom: '2px solid #1e293b', paddingBottom: 4 }}>{form.payee || '—'}</div>
              </div>

              {/* Amount */}
              <div style={{ display: 'flex', gap: 20, marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Amount</div>
                  <div style={{ fontSize: 22, fontWeight: 700, fontFamily: 'monospace', borderBottom: '2px solid #1e293b', paddingBottom: 4 }}>
                    {amount > 0 ? fmt(amount) : '$0.00'}
                  </div>
                </div>
              </div>

              {/* Amount in words */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Amount in Words</div>
                <div style={{ fontSize: 12, fontStyle: 'italic', color: '#374151', borderBottom: '1px solid #d1d5db', paddingBottom: 4 }}>
                  {words || '—'}
                </div>
              </div>

              {/* Memo */}
              {form.memo && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Memo</div>
                  <div style={{ fontSize: 12, color: '#374151' }}>{form.memo}</div>
                </div>
              )}

              {/* Signature line */}
              <div style={{ marginTop: 40, display: 'flex', justifyContent: 'flex-end' }}>
                <div style={{ width: 200, borderTop: '1px solid #1e293b', textAlign: 'center', paddingTop: 4, fontSize: 11, color: '#64748b' }}>
                  Authorized Signature
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
